import { useCallback, useMemo } from 'react';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useAutoCompleteStore } from '@pkgs/components/hooks/stores/useAutoCompleteStore';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { usePromptStore } from '@pkgs/components/hooks/stores/search/usePromptStore';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { useAiSearch } from '@pkgs/components/hooks/queries/useAiSearch';
import { type PromptNumber } from '@pkgs/components/consts/aiSearch';
import { AiStarGradient } from '@pkgs/components/assets/icons/small/AiStarGradient';
import { Overline } from '@ev-tech/ui-core-components/Overline';
import { Copy } from '@ev-tech/ui-core-components/Copy';

import { getAiPromptSuggestionTranslationKey, getRandomNumbersArray } from '../../utils/aiSearchPromptUtils';

import styles from './AiSuggestedPrompts.module.css';

export const AiSuggestedPrompts = ({ onSuggestionClick }: { onSuggestionClick?: () => void }) => {
  const { t } = useAppContext();
  const setPromptValue = usePromptStore((state) => state.setPromptValue);
  const setSearchMode = useSearchStore((state) => state.setSearchMode);
  const setAutoCompleteOptionIndex = useAutoCompleteStore((state) => state.setAutoCompleteOptionIndex);
  const setNumberOfAutoCompleteOptions = useAutoCompleteStore((state) => state.setNumberOfAutoCompleteOptions);
  const setSelectedLokaliseOptionIndex = useAutoCompleteStore((state) => state.setSelectedLokaliseOptionIndex);

  const { mutate } = useAiSearch();

  const suggestionNumbers = useMemo(() => {
    return getRandomNumbersArray();
  }, []);

  const handleSuggestionClick = useCallback(
    (suggestionNumber: PromptNumber, index: number) => {
      const promptValue = t(getAiPromptSuggestionTranslationKey(suggestionNumber));
      setAutoCompleteOptionIndex(index + 1);
      setNumberOfAutoCompleteOptions(suggestionNumbers.length);
      setSelectedLokaliseOptionIndex(suggestionNumber);
      setSearchMode(SearchMode.AI);
      setPromptValue(promptValue);
      onSuggestionClick?.();
      mutate(promptValue);
    },
    [
      t,
      setSearchMode,
      setPromptValue,
      onSuggestionClick,
      mutate,
      setAutoCompleteOptionIndex,
      setNumberOfAutoCompleteOptions,
      setSelectedLokaliseOptionIndex,
      suggestionNumbers.length,
    ],
  );

  return (
    <div className={styles.container}>
      <Overline text={t('search.aiSearch.promptSuggestionsDropdown.trySearchingFor')} />
      {suggestionNumbers.map((suggestionNumber, index) => (
        <button className={styles.promptSuggestion} key={suggestionNumber} onClick={() => handleSuggestionClick(suggestionNumber, index)}>
          <AiStarGradient />
          <Copy text={t(getAiPromptSuggestionTranslationKey(suggestionNumber))} />
        </button>
      ))}
    </div>
  );
};
